import moment from 'moment';

// Load-bearing: moment resolves locale data through a dynamic require that this
// build does not bundle, so `.locale('es')` renders English without it.
import 'moment/locale/es';

// Loading a locale file also activates it globally. Every other moment call in
// the app expects the default; this helper passes the locale per call.
moment.locale('en');

const EMPTY_PLACEHOLDER = '—';

/**
 * Formats an API timestamp for display: "22 de ago. de 2026" (es) /
 * "Aug 22, 2026" (en).
 *
 * The month is a word, not a number, because a localized numeric date renders
 * the same value as 03/04 in es and 04/03 in en.
 *
 * The source column is TIMESTAMPTZ, so the value is an absolute instant and is
 * rendered in the viewer's local time. Backfilled rows are written at midday
 * UTC to keep the calendar day intact in every timezone the platform is used
 * from.
 *
 * @param locale pass `i18n.language`; a regional tag ("es-ES") falls back to
 * its base language on its own.
 * @returns `—` when the value is empty or unparseable, which is how a record
 * that predates its source column reaches the UI.
 */
export const formatApiDate = (value: string | null | undefined, locale: string): string => {
  if (!value) return EMPTY_PLACEHOLDER;
  const date = moment(value);
  return date.isValid() ? date.locale(locale).format('ll') : EMPTY_PLACEHOLDER;
};

/**
 * Formats an API timestamp as ISO `YYYY-MM-DD` for CSV export.
 *
 * ISO rather than the display format so a spreadsheet can parse and sort the
 * column, and empty rather than `—` so an absent value doesn't turn the column
 * into text. Local time, matching the display helper, so the file agrees with
 * what the table shows.
 */
export const formatApiDateForExport = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
};
